import { Edit } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import {
  GridColDef,
  GridRenderCellParams,
  GridFilterModel,
  GridSortModel,
  GridRowSelectionModel
} from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useState } from 'react';

import { InteractionsTemplateListProps } from './InteractionsTemplateList.props';
import { ColorProps } from '../../../../@types';
import { TemplateListResponse } from '../../../../@types/interactions.type';
import { Chip } from '../../../../components/atoms/Chip';
import { CustomDataGrid } from '../../../../components/layouts/CustomDataGrid';
import { ColumnSelectionItem } from '../../../../components/templates/ColumnSelection';
import { PLATFORM_NAME } from '../../../../configs/common';
import { GridFeatureModeConstant } from '../../../../configs/enums';
import { ISACTIVE_FILTER_DROPDOWN_ITEM_ARRAY } from '../../../../constants/common';
import { interactionTemplateColumnSelectionItems } from '../../../../constants/interaction.constant';
import { TabHandler } from '../../../../handlers/TabHandler';
import { useGetAllInteractionTemplates } from '../../../../queries/interactions-query';
import { GridContainer } from '../../../../styled/common.styles';
import { formatDate } from '../../../../utils/common';

export const InteractionsTemplateList: React.FC<InteractionsTemplateListProps> = ({
  handleOpen,
  isRefetch,
  setBackRefetch,
  canEdit
}) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [refetchStatus, setRefetchStatus] = useState(false);
  const [templateList, setTemplateList] = useState<TemplateListResponse>();
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const [sortModel, setSortModel] = useState<GridSortModel>();
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [editedColumnSelectionItems, setEditedColumnSelectionItems] = useState<Array<ColumnSelectionItem>>();

  const { data, isLoading, refetch: refetchTemplatesData, isRefetching } = useGetAllInteractionTemplates({ filterModel, page, pageSize, sortModel });

  const [columns, setColumns] = useState<GridColDef[]>([
    {
      field: 'id',
      headerName: 'Template Id',
      width: 100,
      type: 'number',
      valueFormatter: (value: number) => value?.toString(),
      valueGetter: (_value, row) => row.id
    },
    {
      field: 'name',
      headerName: 'Name',
      type: 'string',
      width: 200,
      valueGetter: (_value, row) => row.name
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      type: 'date',
      width: 200,
      valueGetter: (_value, row) => new Date(row.createdAt),
      valueFormatter: (value: string) => formatDate(value)
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      type: 'string',
      width: 200,
      valueGetter: (_value, row) => row.createdUser
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      type: 'date',
      width: 200,
      valueGetter: (_value, row) => row?.updatedAt && new Date(row.updatedAt),
      valueFormatter: (value: string) => formatDate(value)
    },
    {
      field: 'updatedBy',
      headerName: 'Updated By',
      type: 'string',
      width: 200,
      valueGetter: (_value, row) => row.updatedUser
    },
    {
      field: 'isActive',
      headerName: 'Is Active',
      width: 100,
      type: 'singleSelect',
      valueOptions: ISACTIVE_FILTER_DROPDOWN_ITEM_ARRAY,
      renderCell: (params: GridRenderCellParams) => params.row.isActive ? <Chip label="YES" color={ColorProps.Success} /> : <Chip label="NO" color={ColorProps.Error} />,
      valueGetter: (_value, row) => row.isActive
    },
    {
      field: 'action',
      headerName: '',
      sortable: false,
      width: 50,
      flex: 0.1,
      headerAlign: 'center',
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        canEdit &&
        <Grid>
          <IconButton aria-label="edit" onClick={() => handleOpen(true,params.row)}>
            <Edit />
          </IconButton>
        </Grid>
      )
    }
  ]);

  const onFilterChange = useCallback((filterModel: GridFilterModel) => {
    setFilterModel({ ...filterModel });
  }, []);
  
  const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
    setSortModel(sortModel);
  }, []);

  const handleSelectionModelChange = useCallback((selectionModel: GridRowSelectionModel) => {
    setSelectionModel(selectionModel);
  }, []);

  const handleColumnsChange = (items: Array<ColumnSelectionItem>) => {
    setEditedColumnSelectionItems(items);
  };

  TabHandler(`Interactions Templates | ${PLATFORM_NAME}`);

  useEffect(() => {
    refetchTemplatesData();
  }, [filterModel, page, pageSize, sortModel]);

  useEffect(() => {
    isRefetch && setRefetchStatus(isRefetch);
  }, [isRefetch, refetchStatus]);

  useEffect(() => {
    refetchStatus && refetchTemplatesData();
  }, [refetchStatus]);

  useEffect(() => {
    if (data) {
      setTemplateList(data);
      setRefetchStatus(false);
      setBackRefetch(false);
    }
  }, [data, isRefetching]);

  return (
    <GridContainer>
      <CustomDataGrid
        id="interaction-template-list"
        columns={columns}
        rows={templateList?.data || []}
        exportFileName=""
        enableCSVExport={false}
        columnSelectionItems={editedColumnSelectionItems || interactionTemplateColumnSelectionItems}
        filterMode={GridFeatureModeConstant.server}
        onFilterModelChange={(filterModel: GridFilterModel) => onFilterChange(filterModel)}
        loading={isLoading || isRefetching}
        rowCount={templateList?.total}
        rowsPerPageOptions={[25, 50, 100]}
        pagination={true}
        page={page}
        pageSize={pageSize}
        paginationMode={GridFeatureModeConstant.server}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        sortingMode={GridFeatureModeConstant.server}
        onSortModelChange={handleSortModelChange}
        selectionModel={selectionModel}
        onSelectionModelChange={handleSelectionModelChange}
        handleColumnsChange={handleColumnsChange}
        initialState={{
          columns: {
            columnVisibilityModel: {
              parentNodeId: false,
              referenceNumbers: false,
              status: false,
            },
          },
        }}
        componentsProps={{
          toolbar: {
            printOptions: { disableToolbarButton: true }
          }
        }}
        filterModel={filterModel}
        getRowHeight={() => 'auto'}
        setColumns={setColumns}
      />
    </GridContainer>
  );
};